import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

export interface GraphData {
  '@odata.context': string;
  businessPhones: string[];
  displayName: string;
  givenName: string;
  jobTitle: string;
  mail: string;
  mobilePhone: string | null;
  officeLocation: string;
  preferredLanguage: string;
  surname: string;
  userPrincipalName: string;
  id: string;
}

export interface Application {
  name: string;
  bitId: string;
  dbOps: string;
  appOps: string;
  division: string;
  infraOps: string;
  hostingModel: 'SaaS' | 'Cloud';
  description: string | null;
  isBusinessCritical: boolean;
}

export interface CloudAccount {
  id: string;
  name: string;
  vendor: string;
}

export interface ExtraSourceCloudAccount extends CloudAccount {
  parentCloudAccountId: string;
}

export type AnyValue =
  | string
  | string[]
  | boolean
  | number
  | number[]
  | undefined
  | null
  | Date
  | object
  | ((...args: AnyValue[]) => unknown);

export interface IObject {
  [key: string]:
  | string
  | string[]
  | boolean
  | number
  | number[]
  | undefined
  | null
  | Date
  | JSONDocNode
  | ((...args: IObject[]) => unknown)
  | IObject;
}

export interface EvaluationItem {
  checkpointId: string;
  itemId: string;
  itemType: string;
  reference: string;
  remarks: string;
  status: 'PASS' | 'FAIL' | 'NA';
  system: string;
}

export interface EvaluationMetadata extends Omit<Checkpoint, 'exceptions'> {
  url: string;
  isScored: boolean;
  isMaintenance: boolean;
  isEnabled: boolean;
  displayName: string;
  scorePercent: number;
  totalControls: number;
  group: string | null;
  totalAssessments: number;
  totalPreviewControls: number;
  totalExemptedAssessments: number;
  maintenance: {
    note: string;
    eventTime: string;
  };
}

export interface EvaluationCheckpoint extends CheckpointItem {
  group?: string;
  remarks: string;
  items: EvaluationItem[];
  assessmentData: IObject[];
  metadata: EvaluationMetadata;
  status: 'PASS' | 'FAIL' | 'NA' | 'EXCEPTION' | 'ERROR';
}

export interface CheckpointGroup extends CheckpointItem {
  groupName?: string;
  isActive: boolean;
  checkpoints: EvaluationCheckpoint[];
}

export interface CheckpointItem {
  type: 'group' | 'item';
}

export interface CheckpointDivision {
  title: string;
  checkpoints: CheckpointItem[];
  isActive: boolean;
  bugKey: string;
}

export interface Evaluation {
  id: string;
  score: number;
  bitId: string;
  schemaVersion: string;
  generatedAt: string;
  inclusiveScore: number;
  totalScoredCheckpoints: number;
  totalCheckpoints: number;
  totalEvaluations: number;
  platform: string;
  isLatest: boolean;
  applicationName: string;
  divisionName: string;
  environmentNames: string[];
  cloudAccounts: CloudAccount[];
  extraSourceCloudAccounts: ExtraSourceCloudAccount[];
  isWKTenant: boolean | null;
  previous: Partial<Evaluation>;
  request: EvaluationRequest;
  metadata: Application;
  args: IObject;
  generatedBy: string | null;
  checkpoints: EvaluationCheckpoint[];
}

export interface EvaluationRequest {
  args: IObject;
  bitId: string;
  environmentNames: string[];
  headers: {
    'x-orr-automation-definition': string;
    'x-orr-automation-platform': AutomationPlatform;
    'x-orr-caller': string;
    'user-agent': string;
  };
}

export enum AutomationPlatform {
  ADO = 'azuredevops',
  GITHUB_ACTIONS = 'github',
  JENKINS = 'jenkins',
  OCTOPUS_DEPLOY = 'octopus',
  ORR_PORTAL = 'ORR Portal',
  COMFORMANCE = 'conformance',
  ARGO_CD = 'argocd',
  COPADO = 'copado',
  UNKNOWN = 'unknown',
}

export interface OngoingEvaluation {
  jobId: string;
  statusUrl: string;
  message?: string;
  bitId: string;
  environmentName: string;
  applicationName: string;
  startedAt: string;
  status: 'Running' | 'Failed' | 'Completed' | 'Pending';
  output: EvaluationOutput | null;
}

export interface EvaluationStatusResponse {
  jobId: string | null;
  status: 'Running' | 'Failed' | 'Completed';
  output: EvaluationOutput;
}

export interface EvaluationOutput {
  recordId: string;
  status: string;
  runId: string;
  duration: string;
  bitId: string;
  message: string;
}

export interface TriggerEvaluationRequest {
  bitId: string;
  environments: string | string[];
  PassThreshold: number;
  [x: string]: string | string[] | number;
}

export enum UserRole {
  CHECKPOINT_MANAGER = 'CheckpointManager',
  DR_VIEWER = 'DRView',
}

export interface UserRoleAccess {
  accessibleEndpoints: {
    methods: string[];
    path: string;
    supportedApiVersions: string[];
    versioningURLs: string[];
  }[];
  user: {
    name: string;
    email: string;
    roles: UserRole[];
  };
}
export interface Checkpoint {
  id: string;
  category: string;
  enabled: boolean;
  description: string;
  accountable: string;
  owner: string;
  displayName: string;
  exceptions: Exception[] | null;
  parameters: CheckpointParameter[];
  referenceSystem: string;
  isScored: boolean;
}

export interface Exception {
  id: string;
  approvedAt: string | null;
  approvedBy: string | null;
  createdAt: string;
  createdBy: string | null;
  description: string;
  exception: string;
  exceptionType: string;
  isApproved: string | null;
}
export interface CheckpointParameter {
  paramDescription: string;
  paramName: string;
  displayName: string;
  isOptional: boolean;
  isRuntime: boolean;
  paramValue: string;
  paramType: CheckpointParameterType;
}

export enum CheckpointParameterType {
  String = 'String',
  Boolean = 'Boolean',
  Integer = 'Integer',
  Float = 'Float',
  List = 'List',
  Dictionary = 'Dictionary',
}

export interface CheckpointsStatus {
  checkpointsUnscored: string;
  updates: {
    checkpointId: string;
    comment: string;
    preview: boolean;
    eventTime: string;
    userId: string;
  }[];
}

export interface ErrorResponse {
  status: number;
  title: string;
  traceId: string;
  type: string;
}

export interface JiraCreateMetaField {
  required: boolean;
  schema: {
    type: string;
    system: string;
    custom: string;
  };
  name: string;
  fieldId: string;
  key: string;
  defaultValue: JiraAllowedValue | null;
  hasDefaultValue: boolean;
  operations: string[];
  allowedValues: JiraAllowedValue[];
}

export interface JiraAllowedValue {
  value: string;
  self: string;
  name: string;
  id: string;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
  createmeta: {
    fields: JiraCreateMetaField[];
  };
}
export interface JiraPriority {
  id: string;
  name: string;
  description: string;
  iconUrl: string;
  statusColor: string;
  self: string;
}
